/* eslint-disable no-magic-numbers */
import SingleAxis from 'echarts/lib/coord/single/SingleAxis';
import { Point, Rect } from '../type';

// 根据获取当前coord范围的扩展范围，具体规则为前后各扩展50%跨度，如果遇到全区间极值则处理为全区间
export function getExtendCoord(axis: SingleAxis) {
  const { scale } = axis;
  const { rawExtentInfo } = scale;
  // 计算全量数据范围
  const totalDataRange = [rawExtentInfo._dataMin, rawExtentInfo._dataMax];
  // 计算coord全量范围
  const totalCoordRange: [number, number] = [
    axis.toGlobalCoord(axis.dataToCoord(totalDataRange[0])),
    axis.toGlobalCoord(axis.dataToCoord(totalDataRange[1]))
  ];
  // 获取当前数据范围
  const currDataRange = axis.scale.getExtent();
  // 获取当前coord范围
  const currCoordRange: [number, number] = [
    axis.toGlobalCoord(axis.dataToCoord(currDataRange[0])),
    axis.toGlobalCoord(axis.dataToCoord(currDataRange[1]))
  ];
  const currCoordRangeSpan = currCoordRange[1] - currCoordRange[0];

  const extendCoordRange: [number, number] = [
    Math.max(currCoordRange[0] - currCoordRangeSpan / 2, totalCoordRange[0]),
    Math.min(currCoordRange[1] + currCoordRangeSpan / 2, totalCoordRange[1])
  ];

  return extendCoordRange;
}

export function formatPaddingOrMargin(data: number | number[]) {
  if (typeof data === 'number') {
    return [data, data, data, data];
  }
  if (Array.isArray(data)) {
    const formatData = [...data];
    while (formatData.length < 4) {
      formatData.push(data[formatData.length % data.length]);
    }
    return formatData;
  }
  return [0, 0, 0, 0];
}

export function findPointOnSegment(A: Point, B: Point, C: number): Point | null {
  if (C < Math.min(A[0], B[0]) || C > Math.max(A[0], B[0])) {
    return null;
  }

  let y: number;

  if (A[0] === B[0]) {
    // 线段 AB 垂直于 x 轴
    y = A[1] + ((C - A[0]) * (B[1] - A[1])) / (B[0] - A[0]);
  } else {
    const slope = (B[1] - A[1]) / (B[0] - A[0]);
    const intercept = A[1] - slope * A[0];
    y = slope * C + intercept;
  }

  return [C, y];
}

// 求给定坐标的正交线段与多边形的交点
export function linePolygonIntersection(coord: number, polygon: Point[]): Point[] {
  // 第零步，确认coord是否在多边形内
  const points = polygon.filter(d => d[0] === coord);
  if (points.length > 0) {
    return points;
  }

  // 第一步，确认相邻的俩个coord坐标
  const polygonSorted = polygon.toSorted((a, b) => a[0] - b[0]) as Point[];
  const leftNearCoordIndex = polygonSorted.findLastIndex(p => p[0] < coord);
  const rightNearCoordIndex = polygonSorted.findLastIndex(p => p[0] > coord);
  // 越界判断
  if (leftNearCoordIndex === -1 || rightNearCoordIndex === -1) {
    return [];
  }

  // 第二步，找到俩个相邻coord的点
  // [index-1, index]
  const leftNearPoints = polygonSorted.slice(leftNearCoordIndex - 1, leftNearCoordIndex + 1);
  const rightNearPoints = polygonSorted.slice(rightNearCoordIndex - 1, rightNearCoordIndex + 1);
  leftNearPoints.sort((a, b) => a[1] - b[1]);
  rightNearPoints.sort((a, b) => a[1] - b[1]);

  // 第三步，找到上方的两个点的连线和下方的俩个点的连线
  const LTPoint = leftNearPoints[0];
  const LBPoint = leftNearPoints[1];
  const RTPoint = rightNearPoints[0];
  const RBPoint = rightNearPoints[1];

  // 第四步，求coord与俩个线段的交点
  const TIntersection = findPointOnSegment(LTPoint, RTPoint, coord);
  const BIntersection = findPointOnSegment(LBPoint, RBPoint, coord);

  return [TIntersection, BIntersection];
}

// 计算俩个矩形的重叠面积
export function calculateOverlapArea(rect1: Rect, rect2: Rect): number {
  const xOverlap = Math.max(
    0,
    Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - Math.max(rect1.x, rect2.x)
  );
  const yOverlap = Math.max(
    0,
    Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - Math.max(rect1.y, rect2.y)
  );
  return xOverlap * yOverlap;
}

// 判断点是否在多边形内
export function isPointInPolygon(point: Point, polygon: Point[]) {
  let cnt = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const g = point[0];
    const t = point[1];
    const p1g = polygon[i][0];
    const p1t = polygon[i][1];
    const p2g = polygon[i === polygon.length - 1 ? 0 : i + 1][0];
    const p2t = polygon[i === polygon.length - 1 ? 0 : i + 1][1];
    const ming = Math.min(p1g, p2g);
    const maxg = Math.max(p1g, p2g);
    const mint = Math.min(p1t, p2t);
    const maxt = Math.max(p1t, p2t);
    if (
      // eslint-disable-next-line thsjs/expression-complexity
      mint <= t &&
      t <= maxt &&
      ming <= g &&
      (maxg <= g || p2g + (p1g - p2g) * ((t - p2t) / (p1t - p2t)) < g)
    ) {
      cnt += 1;
    }
  }
  return cnt % 2 === 1;
}

// 判断矩形是否在多边形内，非严格判断
// threshold >=2
export function isRectangleInsidePolygon(rectangle: Rect, points: Point[], threshold = 4): boolean {
  const ltPoint: Point = [rectangle.x, rectangle.y];
  const rtPoint: Point = [rectangle.x + rectangle.width, rectangle.y];
  const rbPoint: Point = [rectangle.x + rectangle.width, rectangle.y + rectangle.height];
  const lbPoint: Point = [rectangle.x, rectangle.y + rectangle.height];

  const lcPoint: Point = [rectangle.x + rectangle.width / 4, rectangle.y + rectangle.height / 2];
  const cPoint: Point = [rectangle.x + rectangle.width / 2, rectangle.y + rectangle.height / 2];
  const rcPoint: Point = [
    rectangle.x + (rectangle.width * 3) / 4,
    rectangle.y + rectangle.height / 2
  ];

  // 避免U/N形、W/M形边缘的情况
  const isCenterInside = [lcPoint, cPoint, rcPoint].every(p => {
    return isPointInPolygon(p, points);
  });

  const count = [ltPoint, rtPoint, rbPoint, lbPoint].filter(point => {
    return isPointInPolygon(point, points);
  }).length;

  return isCenterInside && count >= threshold;
}
